import { useMemo } from 'react';
import { PaymentSummary } from '@/common/helios/UserVehicleFinanceCard/types';
import TransitChargeState from '~/constants/common/TransitChargeState';
import { useVehiclePaymentTerms } from '~/context/vdp/UserVehicleContext';
import useTransitChargeInfo from '~/hooks/vdp/selectors/useTransitChargeInfo';
import useIsHighNetTrade from '~/hooks/vdp/useIsHighNetTrade';
import resolveIncentives from '~/utilities/incentives/resolveIncentives';
import separateDiscounts from '~/utilities/separateDiscounts';

export default function usePaymentSummary(): PaymentSummary {
  const {
    appliedIncentives: appliedIncentiveIds = [],
    apr = 0,
    discounts = [],
    downPayment = 0,
    downPaymentDetails: {
      additionalCashDown = 0,
      totalCashDue = 0,
      tradeDown = 0
    } = {},
    estimatedTaxesAndFees = 0,
    incentivizedPrice: vehiclePrice = 0,
    marketAdjustment = 0,
    marketplaceIncentives = [],
    monthlyPayment = 0,
    msrp = 0,
    term = 0,
    totalDiscountAmount = 0,
    tradeIncentive, // do not default to 0 for proper null checking
    tradeTaxSavings
  } = useVehiclePaymentTerms();
  const isHighNetTrade = useIsHighNetTrade();
  const transitCharge = useTransitChargeInfo();
  const hasTransitCharge = transitCharge.state !== TransitChargeState.Unknown;
  const tradeAmountToUse = isHighNetTrade ? 0 : tradeDown;
  const tradeIncentiveAmountToUse = isHighNetTrade ? 0 : tradeIncentive;
  const incentives = resolveIncentives(
    appliedIncentiveIds,
    marketplaceIncentives
  );
  const incentivesTotal = incentives.reduce(
    (sum, incentive) => sum + incentive.amount,
    0
  );

  const totalPrice =
    vehiclePrice +
    transitCharge.charge +
    estimatedTaxesAndFees -
    totalDiscountAmount -
    tradeAmountToUse -
    (tradeIncentiveAmountToUse ?? 0);

  return useMemo(() => {
    const {
      employeeDiscount,
      geicoDiscount,
      priceReductions,
      rootInsuranceDiscount
    } = separateDiscounts(discounts);

    const credits = {
      downPayment,
      employeeDiscount,
      geicoDiscount,
      incentives,
      marketAdjustment,
      priceReductions,
      rootInsuranceDiscount,
      totalDiscountAmount,
      // incentive amount is inverted to display properly in price breakdown
      totalIncentiveAmount: (incentivesTotal || 0) * -1,
      tradeIncentive: tradeIncentiveAmountToUse,
      tradeInCredit: tradeAmountToUse,
      tradeTaxSavings
    };

    const debits = {
      deliveryCharge: hasTransitCharge ? transitCharge.charge : null,
      msrp,
      taxTitleReg: Math.round(estimatedTaxesAndFees),
      vehiclePrice: Math.round(vehiclePrice)
    };

    return {
      credits,
      debits,
      dueAtOrder: {
        additionalCashDown,
        nonRefundableShippingFees: hasTransitCharge
          ? transitCharge.charge
          : null
      },
      financing: {
        apr,
        monthlyPayment,
        term
      },
      totals: {
        amountFinanced: Math.round(totalPrice - totalCashDue),
        incentivisedPriceWithDiscount: Math.round(
          vehiclePrice - totalDiscountAmount
        ),
        subTotal: Math.round(totalPrice + tradeAmountToUse),
        totalCashDue,
        totalPrice: Math.round(totalPrice),
        totalPriceBeforeTrade: Math.round(totalPrice + tradeAmountToUse)
      }
    };
  }, [
    additionalCashDown,
    apr,
    discounts,
    downPayment,
    estimatedTaxesAndFees,
    hasTransitCharge,
    incentives,
    incentivesTotal,
    marketAdjustment,
    monthlyPayment,
    msrp,
    term,
    totalCashDue,
    totalDiscountAmount,
    totalPrice,
    tradeAmountToUse,
    tradeIncentiveAmountToUse,
    tradeTaxSavings,
    transitCharge.charge,
    vehiclePrice
  ]);
}
